import React, { type ReactNode, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { LanguageDefinition } from '@jwp/ott-common/types/i18n';
import Menu from '@jwp/ott-theme/assets/icons/menu.svg?react';
import SearchIcon from '@jwp/ott-theme/assets/icons/search.svg?react';
import FavouriteIcon from '@jwp/ott-theme/assets/icons/favorite_border.svg?react';
import type { Profile } from '@jwp/ott-common/types/profiles';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import { RELATIVE_PATH_USER_FAVORITES } from '@jwp/ott-common/src/paths';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import DefaultAvatar from '@jwp/ott-theme/assets/profiles/avatar.svg';
import env from '@jwp/ott-common/src/env';

import HeartIcon from '../../../../theme/assets/icons/component-icons/HeartIcon';
import SearchBar, { type Props as SearchBarProps } from '../SearchBar/SearchBar';
import Logo from '../Logo/Logo';
import Button from '../Button/Button';
import UserMenu from '../UserMenu/UserMenu';
import ProfilesMenu from '../ProfilesMenu/ProfilesMenu';
import IconButton from '../IconButton/IconButton';
import LanguageMenu from '../LanguageMenu/LanguageMenu';
import Panel from '../Panel/Panel';
import Icon from '../Icon/Icon';
import ProfileCircle from '../ProfileCircle/ProfileCircle';
import Popover from '../Popover/Popover';
import { useTheme } from '../../../../../ThemeContext';

import styles from './Header.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

type TypeHeader = 'static' | 'fixed';

type Props = {
  headerType?: TypeHeader;
  onMenuButtonClick: () => void;
  logoSrc?: string | null;
  searchBarProps: SearchBarProps;
  searchEnabled: boolean;
  searchActive: boolean;
  onSearchButtonClick?: () => void;
  onCloseSearchButtonClick?: () => void;
  onLoginButtonClick?: () => void;
  onSignUpButtonClick?: () => void;
  openUserPanel: () => void;
  closeUserPanel: () => void;
  openLanguageMenu: () => void;
  closeLanguageMenu: () => void;
  children?: ReactNode;
  isLoggedIn: boolean;
  sideBarOpen: boolean;
  userMenuOpen: boolean;
  languageMenuOpen: boolean;
  canLogin: boolean;
  showPaymentsMenuItem: boolean;
  supportedLanguages: LanguageDefinition[];
  currentLanguage: LanguageDefinition | undefined;
  onLanguageClick: (code: string) => void;
  favoritesEnabled?: boolean;

  profilesData?: {
    currentProfile: Profile | null;
    profiles: Profile[];
    profilesEnabled: boolean;
    selectProfile: ({ avatarUrl, id }: { avatarUrl: string; id: string }) => void;
    isSelectingProfile: boolean;
  };
};

const Header: React.FC<Props> = ({
  children,
  headerType = 'static',
  onMenuButtonClick,
  logoSrc,
  searchBarProps,
  searchActive,
  onSearchButtonClick,
  onLoginButtonClick,
  onCloseSearchButtonClick,
  onSignUpButtonClick,
  isLoggedIn,
  sideBarOpen,
  userMenuOpen,
  languageMenuOpen,
  openUserPanel,
  closeUserPanel,
  openLanguageMenu,
  closeLanguageMenu,
  showPaymentsMenuItem,
  supportedLanguages,
  currentLanguage,
  onLanguageClick,
  favoritesEnabled,
  profilesData: { currentProfile, profiles, profilesEnabled, selectProfile, isSelectingProfile } = {},
}) => {
  const { t } = useTranslation('menu');
  const [logoLoaded, setLogoLoaded] = useState(false);
  const breakpoint = useBreakpoint();
  const {
    isFavoriteEnable,
    isSearchEnabled,
    isSwitchProfileEnable,
    isAuthenticationEnabled,
    isShowLoginButton,
    isShowRegisterButton,
    isMultiLanguageSupport,
    isGuestModeEnabled,
    guestStreamingEnabled,
    isQrAuthenticationEnabled,
    isAnalyticsEnable,
  } = useAppConfig();

  const shouldShowProfileForGuestMode = !isAuthenticationEnabled && isGuestModeEnabled && guestStreamingEnabled && !isQrAuthenticationEnabled;

  const headerClassName = classNames(styles.header, styles[headerType], {
    [styles.searchActive]: searchActive,
  });
  const location = useLocation();
  const isFavoritesPage = location.pathname === '/favorites';
  const { universalStyles } = useTheme();

  const user = useAccountStore((state) => state.user);
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const baseProfileUrl = env.APP_GRAPHQL_ENDPOINT?.replace('graphql', '') || '';
  const deviceType = useGADeviceType();

  // only show the language dropdown when there are other languages to choose from

  const renderSearch = () => {
    if (!isSearchEnabled) return null;

    return searchActive ? (
      <div className={styles.searchContainer}>
        <SearchBar {...searchBarProps} onClose={onCloseSearchButtonClick} />
      </div>
    ) : (
      <IconButton
        className={classNames(styles.iconButton, styles.actionButton)}
        aria-label="Open search"
        onClick={() => {
          if (onSearchButtonClick) {
            onSearchButtonClick();
          }
        }}
      >
        <Icon icon={SearchIcon} />
      </IconButton>
    );
  };

  const renderProfileButtonContent = () => {
    if ((profilesEnabled && currentProfile) || (shouldShowProfileForGuestMode && currentProfile)) {
      return <ProfileCircle src={currentProfile?.avatar_url ? baseProfileUrl + currentProfile?.avatar_url : DefaultAvatar} alt={currentProfile?.name || t('profile_icon')} />;
    }

    if (!shouldShowProfileForGuestMode && user) {
      return (
        <React.Fragment>
          <ProfileCircle src={user?.uAvatarUrl ? baseProfileUrl + user.uAvatarUrl : DefaultAvatar} alt={user.fullName || user.firstName || t('profile_icon')} />
          <div className={styles.profileInfo}>
            <span className={styles.profileName}>{user.fullName || `${user.firstName} ${user.lastName}`.trim()}</span>
            <span className={styles.profileEmail} style={universalStyles.helpText}>
              {user.email}
            </span>
          </div>
          <Icon icon={ChevronRight} className={styles.expandIcon} />
        </React.Fragment>
      );
    }

    return <ProfileCircle src={DefaultAvatar} alt={t('profile_icon')} />;
  };

  const renderUserActions = () => {
    if (!isAuthenticationEnabled && !shouldShowProfileForGuestMode) return null;

    return isLoggedIn || shouldShowProfileForGuestMode ? (
      <React.Fragment>
        {isFavoriteEnable && (
          <>
          <IconButton
            className={classNames(styles.iconButton, styles.actionButton)}
            aria-label={t('open_user_menu')}
            aria-controls="menu_panel"
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            to={RELATIVE_PATH_USER_FAVORITES}
          >
            {isFavoritesPage ? <HeartIcon fillColor={universalStyles.startWatchingButtonBackground.backgroundColor} /> : <Icon icon={FavouriteIcon} />}
          </IconButton>
          <div className={styles.profileDivider} />
          </>
        )}
        <IconButton
          className={classNames(styles.actionButton, styles.profileButton)}
          aria-label={t('open_user_menu')}
          aria-controls="menu_panel"
          aria-expanded={userMenuOpen}
          aria-haspopup="menu"
          onClick={openUserPanel}
          onBlur={closeUserPanel}
        >
          {renderProfileButtonContent()}
        </IconButton>
        <Popover isOpen={userMenuOpen} onClose={closeUserPanel}>
          <Panel id="menu_panel">
            <div onFocus={openUserPanel} onBlur={closeUserPanel}>
              {isSwitchProfileEnable && (
                <ProfilesMenu
                  onButtonClick={closeUserPanel}
                  profiles={profiles ?? []}
                  currentProfile={currentProfile}
                  selectingProfile={!!isSelectingProfile}
                  selectProfile={selectProfile}
                  small
                />
              )}
              <UserMenu
                focusable={userMenuOpen}
                onButtonClick={closeUserPanel}
                showPaymentsItem={showPaymentsMenuItem}
                currentProfile={currentProfile}
                favoritesEnabled={favoritesEnabled}
                small
              />
            </div>
          </Panel>
        </Popover>
      </React.Fragment>
    ) : (
      <div className={styles.buttonContainer}>
        {isShowLoginButton ? (
          <Button
            onClick={onLoginButtonClick}
            label={t('sign_in')}
            aria-haspopup="dialog"
            size="exlarge"
            style={universalStyles.signInButton}
            textStyle={universalStyles.signInButtonText}
          />
        ) : null}
        {isShowRegisterButton ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onSignUpButtonClick}
            label={t('sign_up')}
            aria-haspopup="dialog"
            style={universalStyles.signUpButton}
            textStyle={universalStyles.signUpButtonText}
          />
        ) : null}
      </div>
    );
  };

  const renderLanguageDropdown = () => {
    if (!isMultiLanguageSupport) return null;

    return (
      <LanguageMenu
        className={classNames(styles.iconButton, styles.actionButton)}
        openLanguageMenu={openLanguageMenu}
        closeLanguageMenu={closeLanguageMenu}
        languageMenuOpen={languageMenuOpen}
        onClick={(code) => {
          onLanguageClick(code);
        }}
        languages={supportedLanguages}
        currentLanguage={currentLanguage}
      />
    );
  };

  const handleLogoNavClick = () => {
    if (isAnalyticsEnable) {
      sendGAEvent('screen_view', {
        firebase_screen_class: 'Home',
        firebase_screen: 'Home',
        device_type: deviceType,
        userid_no: userId,
      });
    }
  };

  return (
    <header className={headerClassName} style={universalStyles.navbarBackground}>
      <div className={styles.container}>
        {/* <a href="#content" className={styles.skipToContent}>
          {t('skip_to_content')}
        </a> */}
        <div className={styles.menu}>
          <IconButton
            className={styles.iconButton}
            aria-label={sideBarOpen ? t('close_menu') : t('open_menu')}
            aria-controls="sidebar"
            aria-haspopup="true"
            aria-expanded={sideBarOpen}
            onClick={() => onMenuButtonClick()}
          >
            <Icon icon={Menu} />
          </IconButton>
        </div>
        {logoSrc && (
          <div className={styles.brand} onClick={handleLogoNavClick}>
            <Logo src={logoSrc} onLoad={() => setLogoLoaded(true)} />
          </div>
        )}
        <nav className={styles.nav}>{logoLoaded || !logoSrc ? children : null}</nav>
        <div className={styles.actions}>
          {renderSearch()}
          {renderLanguageDropdown()}
          {renderUserActions()}
        </div>
      </div>
    </header>
  );
};
export default Header;
